import { useState, useEffect } from 'react';

const STORAGE_KEY = 'vera_install_prompt_dismissed_at';
const SHOW_AGAIN_AFTER_DAYS = 7;

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function dismissedRecently() {
  try {
    const ts = Number(localStorage.getItem(STORAGE_KEY));
    if (!ts) return false;
    const days = (Date.now() - ts) / 86400000;
    return days < SHOW_AGAIN_AFTER_DAYS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iOSHelp, setIOSHelp] = useState(false);

  useEffect(() => {
    // Si ya está instalada (standalone) o el usuario la descartó hace poco, no molestar
    if (isStandalone() || dismissedRecently()) return;

    // Android/Chrome/Edge: evento beforeinstallprompt
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari: no soporta beforeinstallprompt — mostrar instructivo manual
    // después de 4s de uso (no apenas entra)
    if (isIOS()) {
      const timer = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  function dismiss() {
    setShow(false);
    setIOSHelp(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  }

  async function install() {
    if (deferredPrompt) {
      // Android / Chrome / Edge — el browser maneja el prompt nativo
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {}
      setDeferredPrompt(null);
      setShow(false);
    } else if (isIOS()) {
      // iOS — mostrar instructivo manual
      setIOSHelp(true);
    }
  }

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: 16, right: 16,
      maxWidth: 400, margin: '0 auto', zIndex: 9500,
      background: 'var(--bg-card)', border: '0.5px solid var(--border-200)',
      borderRadius: 12, padding: 14, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      display: 'flex', alignItems: 'center', gap: 12, fontFamily: 'inherit',
    }}>
      <img src="/icons/icon-192.png" alt="" width={44} height={44}
        style={{ borderRadius: 10, flexShrink: 0 }} />
      {iOSHelp ? (
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>
            Para instalar Vera en iPhone:
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-500)', lineHeight: 1.5 }}>
            1. Toca el botón <b>Compartir</b> ⎙ en Safari<br/>
            2. Selecciona <b>"Agregar a pantalla de inicio"</b>
          </div>
          <button type="button" onClick={dismiss}
            style={{ marginTop: 8, padding: '5px 10px', fontSize: 11,
              border: '0.5px solid var(--border-200)', borderRadius: 6,
              background: 'transparent', color: 'var(--ink-400)', cursor: 'pointer', fontFamily: 'inherit' }}>
            Entendido
          </button>
        </div>
      ) : (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>
              Instala Vera en tu celular
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>
              Acceso rápido sin abrir el browser
            </div>
          </div>
          <button type="button" onClick={dismiss}
            style={{ padding: '6px 10px', fontSize: 12,
              border: '0.5px solid var(--border-200)', borderRadius: 6,
              background: 'transparent', color: 'var(--ink-400)', cursor: 'pointer', fontFamily: 'inherit' }}>
            Después
          </button>
          <button type="button" onClick={install}
            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600,
              border: 'none', borderRadius: 6,
              background: 'var(--accent, #FF6428)', color: 'white',
              cursor: 'pointer', fontFamily: 'inherit' }}>
            Instalar
          </button>
        </>
      )}
    </div>
  );
}
